import { useEffect, useRef, useState } from 'react';
import { useTelegramWebApp } from './useTelegramWebApp';
import { voleeApi } from '../api/VoleeApi';
import { setToken, getToken, setLoggedIn, setProfile, setRole, setFirstLogin, resetToken, resetUserData } from '../services/auth';
import { useCommunity } from '../context/community';
import { getTelegramStartParam, getTelegramStartParamContext, isTelegramMiniAppEnvironment } from '../services/telegramStartParam';
import { PAGES } from '../routes/PAGES';

const COMMUNITY_OPTION_PUBLIC_EVENTS_PAGE = 'PUBLIC_EVENTS_PAGE';
const JOIN_START_PARAM_PREFIX = 'join_';

const hasCommunityOption = (communityOptions, optionName) => {
    if (!Array.isArray(communityOptions)) {
        return false;
    }
    return communityOptions.includes(optionName);
};

/**
 * Hook to handle Telegram mini-app auto-login
 * Automatically logs in user if they have linked telegramChatId
 * @returns {Object} { isChecking: boolean, isAuthenticated: boolean, hasAttempted: boolean, error?: string }
 */
export const useTelegramAutoLogin = () => {
    const { isTelegramWebApp, initData } = useTelegramWebApp();
    const { community } = useCommunity();
    const [isChecking, setIsChecking] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [error, setError] = useState(null);
    const [hasAttempted, setHasAttempted] = useState(false);
    const autoLoginTriedRef = useRef(false);

    useEffect(() => {
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
        const isOnLoginPage = currentPath === PAGES.LOGIN;
        const isOnRegistrationPage = currentPath === PAGES.REGISTRATION;
        const isOnOneTimeCodePage = currentPath === PAGES.ONE_TIME_CODE;
        const isOnAuthMagicPage = currentPath === PAGES.AUTH_MAGIC;
        const isOnWelcomePage = currentPath === PAGES.WELCOME || currentPath === '/';

        const finishWithoutAutoLogin = () => {
            setIsChecking(false);
            setHasAttempted(true);
        };

        // telegram-web-app.js defines WebApp in a normal browser too; only Mini App launch data counts.
        if (!isTelegramMiniAppEnvironment()) {
            finishWithoutAutoLogin();
            return;
        }

        const { hasLiveStartParam } = getTelegramStartParamContext();
        const hasFailedAttempt = sessionStorage.getItem('telegramAutoLoginFailed') === 'true';

        // Magic-link login owns this page. Waiting for initData/miniappInit here
        // leaves App.js on the Telegram loader and AuthMagic never mounts.
        if (isOnAuthMagicPage) {
            finishWithoutAutoLogin();
            return;
        }

        // Login page usually skips auto-login to avoid loops; keep trying when a live
        // start_param still needs auth (queue / chats / profile deep links).
        if (isOnLoginPage && !hasLiveStartParam) {
            finishWithoutAutoLogin();
            return;
        }

        // Email OTP flow: never call miniappInit here — failure sets loginError + hard redirect and breaks OTP in Telegram WebView.
        if (isOnOneTimeCodePage) {
            finishWithoutAutoLogin();
            return;
        }

        if (hasFailedAttempt) {
            finishWithoutAutoLogin();
            return;
        }

        if (autoLoginTriedRef.current) {
            return;
        }

        // Already logged in: do not wait for Telegram initData (it is often empty on Telegram Web).
        const existingToken = getToken();
        if (existingToken) {
            autoLoginTriedRef.current = true;
            setIsChecking(false);
            setIsAuthenticated(true);
            setHasAttempted(true);
            return;
        }

        if (!initData) {
            // Keep isChecking=true so App shows loader until initData arrives; avoids showing "Log In" before auth resolves
            setIsChecking(true);

            // Unblock after timeout so we don't show loader forever if initData never arrives (e.g. opened in browser)
            const unblockMs = 4000;
            const timeoutId = setTimeout(() => {
                setIsChecking(false);
                setHasAttempted(true);
            }, unblockMs);
            return () => clearTimeout(timeoutId);
        }

        autoLoginTriedRef.current = true;

        // Attempt auto-login
        const attemptAutoLogin = async () => {
            setIsChecking(true);
            setError(null);

            try {
                const response = await voleeApi.telegram.miniappInit(initData);

                if (response.authenticated && response.token && response.profile) {
                    // User is authenticated - set token and profile
                    setToken(response.token);
                    setLoggedIn();

                    // Set user profile
                    if (response.profile) {
                        setProfile(response.profile);

                        // Set role if available
                        if (response.profile.role) {
                            setRole(response.profile.role);
                        }

                        // Set firstLogin if available
                        if (response.profile.firstLogin !== undefined) {
                            setFirstLogin(response.profile.firstLogin);
                        }
                    }

                    setIsAuthenticated(true);
                } else {
                    // Store pending token for auto-connect during registration (including join_ event links)
                    const startParam = getTelegramStartParam();
                    const cameViaJoinLink = typeof startParam === 'string' && startParam.startsWith(JOIN_START_PARAM_PREFIX);
                    const hasPublicEventsPage = hasCommunityOption(community?.options, COMMUNITY_OPTION_PUBLIC_EVENTS_PAGE);

                    if (response.pendingToken) {
                        sessionStorage.setItem('telegramPendingToken', response.pendingToken);
                    }

                    // Check if we're on registration page or event page (public access)
                    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
                    const isOnRegistrationPage = currentPath === '/registration';
                    const isOnEventPage = currentPath.startsWith('/event/');

                    // Check if we have start_param (user came from event button)
                    const hasStartParam = getTelegramStartParam();

                    setIsAuthenticated(false);
                    setError(response.reason || 'telegram_not_linked');

                    if (isOnRegistrationPage) {
                        // If on registration page, don't redirect - let user register
                        // The pending token is already stored for auto-connect during registration
                    } else if (isOnWelcomePage) {
                        // Landing / welcome: allow email ShortAuth without bogus "Account not found" from Telegram-only flow
                    } else if ((isOnEventPage && hasStartParam) || (cameViaJoinLink && hasPublicEventsPage)) {
                        // User is on event page with start_param - allow public access to event
                        // Don't redirect to login, let EventDetail show event publicly
                    } else {
                        // User is not linked and not on registration/event page.
                        // Telegram binding is optional: do not force redirect/error, allow normal email auth flow.
                        // Mark that we've attempted and failed (to prevent infinite loop)
                        sessionStorage.setItem('telegramAutoLoginFailed', 'true');

                        // Reset token and user data
                        resetToken();
                        resetUserData();
                    }
                }
            } catch (err) {
                setError(err.msg || err.message || 'auto_login_failed');
                setIsAuthenticated(false);

                // Check if we're on registration page, event page, or login page
                const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
                const isOnRegistrationPage = currentPath === '/registration';
                const isOnLoginPage = currentPath === '/login';
                const isOnEventPage = currentPath.startsWith('/event/');

                // Check if we have start_param (user came from event button)
                const hasStartParam = getTelegramStartParam();
                const startParam = hasStartParam || '';
                const cameViaJoinLink = typeof startParam === 'string' && startParam.startsWith(JOIN_START_PARAM_PREFIX);
                const hasPublicEventsPage = hasCommunityOption(community?.options, COMMUNITY_OPTION_PUBLIC_EVENTS_PAGE);

                if (isOnRegistrationPage) {
                    // If on registration page, don't redirect - let user register
                } else if (isOnWelcomePage) {
                } else if ((isOnEventPage && hasStartParam) || (cameViaJoinLink && hasPublicEventsPage)) {
                    // User is on event page with start_param - allow public access to event
                    // Don't redirect to login, let EventDetail show event publicly
                } else if (!isOnLoginPage) {
                    // Mark that we've attempted and failed (to prevent infinite loop)
                    sessionStorage.setItem('telegramAutoLoginFailed', 'true');

                    // Telegram binding is optional: keep user on current flow instead of forcing /login.
                    resetToken();
                    resetUserData();
                }
            } finally {
                setIsChecking(false);
                setHasAttempted(true); // Mark as attempted after completion
            }
        };

        attemptAutoLogin();
    }, [isTelegramWebApp, initData, community?.options]);

    return { isChecking, isAuthenticated, hasAttempted, error };
};

